'use client';

import { type ReactNode, createContext, use, useCallback, useRef, useState } from 'react';

interface FaqExpandState {
    countLimit: number;
    hasMore: boolean;
    showButton: boolean;
    handleShowMore: () => void;
}

const FaqExpandContext = createContext<FaqExpandState>(undefined as unknown as FaqExpandState);

export interface FaqExpandProviderProps {
    children: ReactNode;
    faqsLength: number;
    limit: number;
}

export function FaqExpandProvider({ children, faqsLength, limit }: FaqExpandProviderProps) {
    const [countLimit, setCountLimit] = useState(limit);
    const wrapperDivRef = useRef<HTMLDivElement>(null);

    const handleShowMore = useCallback((): void => {
        setCountLimit((prev) => {
            if (prev >= faqsLength) {
                // Reset to initial limit
                return limit;
            }

            const nextFaqsStep = limit > 0 ? limit : 5;
            const newLimit = prev + nextFaqsStep;
            if (newLimit < faqsLength) {
                return newLimit;
            }
            return faqsLength;
        });
    }, [faqsLength, limit]);

    const showButton = faqsLength > limit;
    const hasMore = faqsLength > countLimit;

    return (
        <>
            <div ref={wrapperDivRef}></div>
            <FaqExpandContext value={{ countLimit, hasMore, showButton, handleShowMore }}>{children}</FaqExpandContext>
        </>
    );
}

export function useFaqExpandState() {
    const context = use(FaqExpandContext);
    if (!context) {
        throw new Error('useFaqExpandState must be used within a FaqExpandProvider');
    }

    return context;
}
